import { circle } from './circle';
import { segment } from './segment';
import {
  center,
  endpoints,
  liesOn,
  midpoint,
  parameterOf,
  period,
  piece,
  type Shape,
} from './shape';
import { dist, type Vec2 } from './vec2';

/** Parameters closer than this to an end are that end. */
const EPS = 1e-7;

/** How far, in world units, `extensionOf` carries a line past each end. */
const REACH = 1e6;

export type End = 'start' | 'end';

/**
 * The two pieces of `shape` either side of `to`, taken along its own carrier, named by the
 * end each keeps. Null when `to` is not between the ends, or the shape is closed.
 */
export function splitAt(shape: Shape, to: Vec2): Readonly<Record<End, Shape>> | null {
  if (endpoints(shape).length === 0) return null;
  const t = parameterOf(shape, to);
  if (t <= EPS || t >= 1 - EPS) return null;
  return { start: piece(shape, 0, t), end: piece(shape, t, 1) };
}

/**
 * Which side of the cut at `cut` the point `p` is on. Off an arc, in the gap between its ends,
 * the nearer end across the gap decides.
 */
export function sideOf(shape: Shape, cut: Vec2, p: Vec2): End {
  const t = parameterOf(shape, p);
  const cycle = period(shape);
  if (cycle !== null && t > 1) return t - 1 <= cycle - t ? 'end' : 'start';
  return t < parameterOf(shape, cut) ? 'start' : 'end';
}

/**
 * `shape` with the end `to` lies beyond lengthened to it, `to` taken along the shape's own
 * carrier. On an arc, the gap is split at its middle: past that, `to` is before the start.
 * Null when `to` is on the shape itself, or the shape is closed and has no end.
 */
export function extendTo(shape: Shape, to: Vec2): Shape | null {
  if (endpoints(shape).length === 0) return null;
  const t = parameterOf(shape, to);
  const cycle = period(shape);
  if ((t >= -EPS && t <= 1 + EPS) || (cycle !== null && t >= cycle - EPS)) return null;
  if (cycle === null) return t > 1 ? piece(shape, 0, t) : piece(shape, t, 1);
  return t - 1 <= cycle - t ? piece(shape, 0, t) : piece(shape, t - cycle, 1);
}

/**
 * Everything `shape` can be extended along: a line carried far past both ends, or an arc's
 * whole circle. Null for a closed shape, which has no end.
 */
export function extensionOf(shape: Shape): Shape | null {
  const [a, b] = endpoints(shape);
  if (!a || !b) return null;
  const cycle = period(shape);
  if (cycle !== null) return piece(shape, 0, cycle);
  const reach = REACH / Math.max(dist(a, b), EPS);
  return piece(shape, -reach, 1 + reach);
}

/**
 * How far, in world units, a point may sit off a carrier and still count as on it. Fields are
 * stored to four decimals, so a piece cut from a slanted line leans off it by about that much.
 */
const JOIN_TOLERANCE = 1e-4;

/**
 * `a` and `b` as one shape running the way `a` does: two lines on one line, or two arcs on
 * one circle, that touch or overlap. Arcs that cover the circle close into it. Pieces are
 * measured against the longer one, since a short one's stored ends tilt it most. Null for
 * anything else, including two pieces with a gap between them.
 */
export function joinShapes(a: Shape, b: Shape): Shape | null {
  if (a.type !== b.type || endpoints(a).length === 0 || endpoints(b).length === 0) return null;
  const [base, other] = chord(a) >= chord(b) ? [a, b] : [b, a];
  const guide = extensionOf(base);
  const [o0, o1] = endpoints(other);
  const mid = midpoint(other);
  if (!guide || !o0 || !o1 || !mid) return null;
  if (![o0, o1, mid].every((p) => liesOn(guide, p, JOIN_TOLERANCE))) return null;

  const t0 = parameterOf(base, o0);
  const t1 = parameterOf(base, o1);
  const cycle = period(base);
  let lo = cycle === null ? Math.min(t0, t1) : t0;
  let hi = cycle === null ? Math.max(t0, t1) : t0 + ((((t1 - t0) % cycle) + cycle) % cycle);
  if (cycle !== null && lo > 1 + EPS && hi >= cycle - EPS) {
    lo -= cycle;
    hi -= cycle;
  }
  if (lo > 1 + EPS || hi < -EPS) return null;
  lo = Math.min(lo, 0) > -EPS ? 0 : lo;
  hi = Math.max(hi, 1) < 1 + EPS ? 1 : hi;

  const middle = center(base);
  if (cycle !== null && middle && hi - lo >= cycle - EPS) {
    return circle(middle, dist(middle, o0));
  }
  if (cycle !== null) return piece(base, lo, hi);

  // A line's ends are the outermost of the four it is joined from, so none of them moves.
  const along = [...endpoints(base), o0, o1].sort(
    (p, q) => parameterOf(base, p) - parameterOf(base, q),
  );
  const [first, last] = [along[0]!, along[3]!];
  const [a0, a1] = endpoints(a) as [Vec2, Vec2];
  return parameterOf(base, a1) < parameterOf(base, a0)
    ? segment(last, first)
    : segment(first, last);
}

function chord(shape: Shape): number {
  const [p, q] = endpoints(shape) as [Vec2, Vec2];
  return dist(p, q);
}
